import { Box, Flex, Heading, Text, VStack } from '@chakra-ui/react';
import { useMemo } from 'react';
import { SettingsCard } from '../../components/settings/primitives';
import { Icon } from '../../ui';
import {
    PARTBAY_BLUE,
    PARTBAY_BLUE_TINT,
    SETTINGS_BORDER_COLOR,
    SETTINGS_CONTENT_MAX_WIDTH,
} from '../../components/settings/primitives/constants';
import {
    getEntryDescription,
    matchesSearch,
    type SettingsAccessContext,
    type SettingsGroup,
    type SettingsNavEntry,
} from './settingsNav';

export interface SettingsOverviewProps {
    groups: Array<{ group: SettingsGroup; entries: SettingsNavEntry[] }>;
    accessContext: SettingsAccessContext;
    /** Search text, owned by the settings header so it works from any screen. */
    query: string;
    onSelect: (id: string) => void;
}

/** One section, as a full-width row inside its group's card. */
const SectionRow = ({
    entry,
    description,
    onSelect,
}: {
    entry: SettingsNavEntry;
    description: string;
    onSelect: (id: string) => void;
}) => (
    <Flex
        as="button"
        type="button"
        w="100%"
        textAlign="left"
        align="center"
        gap={4}
        px={5}
        py={4}
        role="group"
        transition="background 0.15s"
        _hover={{ bg: 'gray.50' }}
        onClick={() => onSelect(entry.id)}
    >
        <Flex
            align="center"
            justify="center"
            boxSize={9}
            borderRadius="md"
            bg={PARTBAY_BLUE_TINT}
            flexShrink={0}
        >
            <Icon name={entry.icon} size={5} color={PARTBAY_BLUE} />
        </Flex>
        <Box flex={1} minW={0}>
            <Text fontSize="sm" fontWeight="medium" color="gray.800">
                {entry.label}
            </Text>
            <Text fontSize="sm" color="gray.500" mt={0.5}>
                {description}
            </Text>
        </Box>
        <Icon
            name="chevronRight"
            size={5}
            color="gray.400"
            flexShrink={0}
            _groupHover={{ color: 'gray.600' }}
        />
    </Flex>
);

/**
 * Landing screen for Settings: every section the user can reach, as a row in a
 * stacked list, grouped the same way as the sidebar. The search box filters
 * across all sections at once, matching section names and the settings inside
 * them. Content sits in the same centered, fixed-width column as the form
 * sections so the overview lines up with the pages it links to.
 */
const SettingsOverview = ({ groups, accessContext, query, onSelect }: SettingsOverviewProps) => {
    const filteredGroups = useMemo(
        () =>
            groups
                .map(({ group, entries }) => ({
                    group,
                    entries: entries.filter((entry) => matchesSearch(entry, query, accessContext)),
                }))
                .filter((bucket) => bucket.entries.length > 0),
        [groups, query, accessContext],
    );

    return (
        <Box h="100%" overflowY="auto" px={8} pt={6} pb={8} w="100%">
            <Box maxW={SETTINGS_CONTENT_MAX_WIDTH} mx="auto">
                <Box pb={6}>
                    <Heading size="md" color="gray.800">
                        Settings
                    </Heading>
                    <Text fontSize="sm" color="gray.500" mt={1}>
                        Manage your profile, your organization, and how Yaxle orders parts
                    </Text>
                </Box>

                {filteredGroups.length === 0 ? (
                    <Flex
                        direction="column"
                        align="center"
                        justify="center"
                        py={16}
                        borderWidth="1px"
                        borderColor={SETTINGS_BORDER_COLOR}
                        borderRadius="lg"
                        bg="white"
                    >
                        <Text fontSize="sm" color="gray.600" fontWeight="medium">
                            No settings match "{query}"
                        </Text>
                        <Text fontSize="sm" color="gray.500" mt={1}>
                            Try a different term, like "tax", "vendor", or "notifications"
                        </Text>
                    </Flex>
                ) : (
                    <VStack spacing={8} align="stretch">
                        {filteredGroups.map(({ group, entries }) => (
                            <Box key={group.id}>
                                <Text
                                    fontSize="xs"
                                    fontWeight="bold"
                                    color="gray.500"
                                    letterSpacing="wider"
                                    textTransform="uppercase"
                                    mb={3}
                                >
                                    {group.label}
                                </Text>
                                <SettingsCard isFlush>
                                    {entries.map((entry) => (
                                        <SectionRow
                                            key={entry.id}
                                            entry={entry}
                                            description={getEntryDescription(entry, accessContext)}
                                            onSelect={onSelect}
                                        />
                                    ))}
                                </SettingsCard>
                            </Box>
                        ))}
                    </VStack>
                )}
            </Box>
        </Box>
    );
};

export default SettingsOverview;
